import ArrowLeft from '@oberoninternal/travelbase-ds/components/figure/ArrowLeft';
import ArrowRight from '@oberoninternal/travelbase-ds/components/figure/ArrowRight';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import useIntervalClick from '@oberoninternal/travelbase-ds/hooks/useIntervalClick';
import { FlexProps } from '@rebass/grid';
import React, { FC, useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { PlannerViewActionsContext, PlannerViewContext } from './Planner';
import { useDeviceSize } from '@oberoninternal/travelbase-ds/context/devicesize';

interface Props extends FlexProps {
    loading?: boolean;
    onToday: () => void;
}
const ActivityPlannerController: FC<React.PropsWithChildren<Props>> = ({ loading, onToday }) => {
    const plannerViewActions = useContext(PlannerViewActionsContext);
    const plannerViewState = useContext(PlannerViewContext);
    const deviceSize = useDeviceSize();

    if (!plannerViewActions || !plannerViewState) {
        throw new Error('PlannerControls can only be used within a Planner component');
    }

    const { scrollBy, getMonthDimensions, scrollToToday } = plannerViewActions;
    const { monthsInView } = plannerViewState;
    const [month, setMonth] = useState(0);

    const useScrollByMonthWidth = (positive = true) =>
        useIntervalClick(() => {
            if (positive) {
                setMonth(month! + 1);
            } else {
                setMonth(month! - 1);
            }
            const monthWidth = Math.floor(getMonthDimensions());
            scrollBy(positive ? monthWidth : -monthWidth, !loading);
        }, 500);

    const incrementScrollProps = useScrollByMonthWidth();
    const decrementScrollProps = useScrollByMonthWidth(false);

    const toTodayMobile = () => {
        scrollToToday();
        setMonth(0);
    };

    const toToday = () => {
        setMonth(0);
        onToday();
    };

    return (
        <ControlsWrapper>
            <ControlsHeader>
                <HeaderLeabel>
                    <FormattedMessage defaultMessage="Selecteer een datum" />
                </HeaderLeabel>
                {/* Component has different behaviour on mobile size */}
                {deviceSize === 'mobile' && (
                    <StyledTextButton onClick={() => toTodayMobile()} disabled={month === 0}>
                        <FormattedMessage defaultMessage="Vandaag" />
                    </StyledTextButton>
                )}

                {deviceSize !== 'mobile' && (
                    <StyledTextButton onClick={() => toToday()} disabled={monthsInView?.[0] === 0}>
                        <FormattedMessage defaultMessage="Vandaag" />
                    </StyledTextButton>
                )}
            </ControlsHeader>
            <Controls>
                {/* For now you can only go to today if the current month is loaded */}
                {deviceSize === 'mobile' && (
                    <StyledRoundButton disabled={month === 0} {...decrementScrollProps}>
                        <ArrowLeft />
                    </StyledRoundButton>
                )}
                {deviceSize !== 'mobile' && (
                    <StyledRoundButton disabled={monthsInView?.[0] === 0} {...decrementScrollProps}>
                        <ArrowLeft />
                    </StyledRoundButton>
                )}
                <StyledRoundButton {...incrementScrollProps}>
                    <ArrowRight />
                </StyledRoundButton>
            </Controls>
        </ControlsWrapper>
    );
};

export default ActivityPlannerController;

const ControlsWrapper = styled.div``;

const ControlsHeader = styled.div`
    margin-bottom: 3rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const HeaderLeabel = styled(Body)`
    font-weight: 700;
    font-size: 2rem;
    line-height: 2.6rem;
`;

const Controls = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-left: auto;
    > button + button {
        margin-left: ${({ theme }) => theme.spacing['30_Small']};
    }
`;

const StyledRoundButton = styled.button`
    opacity: ${({ disabled }) => (disabled ? '0.4' : '1')};
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    svg {
        height: 1.5rem;
        width: 1.5rem;
    }
`;

const StyledTextButton = styled.button`
    background: ${({ theme }) => theme.colors.neutral[5]};
    border-radius: 9.9rem;
    padding: 0.4rem 1.6rem;
    font-weight: 600;
    font-size: 1.4rem;
    color: ${({ theme }) => theme.colors.neutral[90]};
    opacity: ${({ disabled }) => (disabled ? '0.4' : '1')};
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

    :hover {
        text-decoration: none !important;
        color: ${({ theme }) => theme.colors.neutral[90]};
    }
`;
